/**
 * AutoActionBlockMarkRejectAlertOnFraudPage — fraud auto-response console.
 *
 * Four panels:
 *  1. Action-threshold rules: each action (block/mark/reject/alert) has a trigger
 *     risk-score threshold, target subject-scope, and a client-side enable toggle.
 *     (The backend derives action from risk_score deterministically; rule config
 *     here is an operator reference + local preview scope for the Decide form.)
 *  2. Rollup tiles: counts per persisted action pulled from `/summary`.
 *  3. Decide-and-record form: POST a fraud event to run the server rule engine
 *     and record the resulting action (or skip when clean).
 *  4. Recent-action feed: the last 100 recorded actions from `GET /`.
 *
 * Accent: green-only for success / safe states. Red is reserved for errors and
 * high-severity/block outcomes.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAutoActionBlockMarkRejectAlertOnFraudCreate,
	useAutoActionBlockMarkRejectAlertOnFraudList,
	useAutoActionBlockMarkRejectAlertOnFraudSummary,
} from "./hooks";
import type {
	FraudAction,
	FraudActionRow,
	FraudSeverity,
	FraudSubjectType,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-semibold uppercase tracking-wider text-zinc-600";

const PERSISTED_ACTIONS: readonly Exclude<FraudAction, "clean">[] = [
	"block",
	"mark",
	"reject",
	"alert",
] as const;

const SUBJECT_TYPES: readonly FraudSubjectType[] = [
	"click",
	"conversion",
	"ip",
	"user",
] as const;

interface RuleConfig {
	action: Exclude<FraudAction, "clean">;
	/** Server-equivalent default threshold from decideAction(). */
	threshold: number;
	scope: "any" | FraudSubjectType;
	enabled: boolean;
}

const DEFAULT_RULES: RuleConfig[] = [
	{ action: "block", threshold: 90, scope: "any", enabled: true },
	{ action: "reject", threshold: 70, scope: "conversion", enabled: true },
	{ action: "mark", threshold: 40, scope: "click", enabled: true },
	{ action: "alert", threshold: 20, scope: "any", enabled: true },
];

/** Green-first badge. Block & critical get the single red accent. */
function actionBadgeClass(action: FraudAction): string {
	if (action === "block") {
		return "inline-flex items-center rounded-[4px] border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-red-700";
	}
	return "inline-flex items-center rounded-[4px] border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-green-800";
}

function severityBadgeClass(sev: FraudSeverity): string {
	if (sev === "critical") {
		return "inline-flex items-center rounded-[4px] border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700";
	}
	return "inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-700";
}

function formatTime(unixSeconds: number): string {
	if (!unixSeconds) return "";
	const d = new Date(unixSeconds * 1000);
	if (Number.isNaN(d.getTime())) return "";
	return d.toISOString().replace("T", " ").slice(0, 19);
}

export function AutoActionBlockMarkRejectAlertOnFraudPage() {
	const listQuery = useAutoActionBlockMarkRejectAlertOnFraudList();
	const summaryQuery = useAutoActionBlockMarkRejectAlertOnFraudSummary();
	const createMut = useAutoActionBlockMarkRejectAlertOnFraudCreate();

	const [rules, setRules] = useState<RuleConfig[]>(DEFAULT_RULES);

	const [eventId, setEventId] = useState("");
	const [subjectType, setSubjectType] = useState<FraudSubjectType>("click");
	const [subjectId, setSubjectId] = useState("");
	const [riskScore, setRiskScore] = useState("50");
	const [reason, setReason] = useState("");

	const toggleRule = (action: Exclude<FraudAction, "clean">) => {
		setRules((prev) =>
			prev.map((r) =>
				r.action === action ? { ...r, enabled: !r.enabled } : r,
			),
		);
	};

	const updateThreshold = (
		action: Exclude<FraudAction, "clean">,
		value: number,
	) => {
		setRules((prev) =>
			prev.map((r) => (r.action === action ? { ...r, threshold: value } : r)),
		);
	};

	const updateScope = (
		action: Exclude<FraudAction, "clean">,
		scope: RuleConfig["scope"],
	) => {
		setRules((prev) =>
			prev.map((r) => (r.action === action ? { ...r, scope } : r)),
		);
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!eventId.trim() || !subjectId.trim()) return;
		const scoreNum = Number(riskScore);
		if (!Number.isFinite(scoreNum) || scoreNum < 0) return;
		createMut.mutate(
			{
				event_id: eventId.trim(),
				subject_type: subjectType,
				subject_id: subjectId.trim(),
				risk_score: Math.floor(scoreNum),
				reason: reason.trim() || undefined,
			},
			{
				onSuccess: () => {
					setEventId("");
					setSubjectId("");
					setReason("");
				},
			},
		);
	};

	const items: FraudActionRow[] = listQuery.data?.items ?? [];

	const summaryByAction = useMemo(() => {
		const map = new Map<Exclude<FraudAction, "clean">, number>();
		for (const a of PERSISTED_ACTIONS) map.set(a, 0);
		if (summaryQuery.data) {
			for (const b of summaryQuery.data.by_action) {
				map.set(b.action, b.count);
			}
		}
		return map;
	}, [summaryQuery.data]);

	return (
		<main
			data-testid="auto-action-block-mark-reject-alert-on-fraud-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className={LABEL}>Fraud auto-response</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Auto-action on fraud
				</h1>
				<p className="text-sm text-zinc-600">
					Map incoming fraud signals to automated actions and review recent
					decisions.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Action rules"
				data-testid="auto-action-block-mark-reject-alert-on-fraud-rules"
			>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Action rules
					</h2>
					<span className="text-xs text-zinc-500">
						Server thresholds shown as defaults.
					</span>
				</div>
				<div className="mt-3 overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
							<tr>
								<th className="px-3 py-2 font-medium">Action</th>
								<th className="px-3 py-2 font-medium">Trigger {">="}</th>
								<th className="px-3 py-2 font-medium">Scope</th>
								<th className="px-3 py-2 font-medium">Enabled</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200 bg-white">
							{rules.map((rule) => (
								<tr
									key={rule.action}
									data-testid={`auto-action-block-mark-reject-alert-on-fraud-rule-${rule.action}`}
								>
									<td className="px-3 py-2">
										<span className={actionBadgeClass(rule.action)}>
											{rule.action}
										</span>
									</td>
									<td className="px-3 py-2">
										<input
											type="number"
											min={0}
											max={100}
											value={rule.threshold}
											onChange={(e) =>
												updateThreshold(
													rule.action,
													Number(e.currentTarget.value),
												)
											}
											className="w-20 rounded-[4px] border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600/30"
											data-testid={`auto-action-block-mark-reject-alert-on-fraud-threshold-${rule.action}`}
										/>
									</td>
									<td className="px-3 py-2">
										<select
											value={rule.scope}
											onChange={(e) =>
												updateScope(
													rule.action,
													e.currentTarget.value as RuleConfig["scope"],
												)
											}
											className="rounded-[4px] border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600/30"
											data-testid={`auto-action-block-mark-reject-alert-on-fraud-scope-${rule.action}`}
										>
											<option value="any">any</option>
											{SUBJECT_TYPES.map((s) => (
												<option key={s} value={s}>
													{s}
												</option>
											))}
										</select>
									</td>
									<td className="px-3 py-2">
										<label className="inline-flex cursor-pointer items-center gap-2">
											<input
												type="checkbox"
												checked={rule.enabled}
												onChange={() => toggleRule(rule.action)}
												className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600/40"
												data-testid={`auto-action-block-mark-reject-alert-on-fraud-toggle-${rule.action}`}
											/>
											<span className="text-xs text-zinc-700">
												{rule.enabled ? "on" : "off"}
											</span>
										</label>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Action totals"
				data-testid="auto-action-block-mark-reject-alert-on-fraud-totals"
			>
				<h2 className="text-base font-semibold text-zinc-900">Totals</h2>
				<div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
					{PERSISTED_ACTIONS.map((a) => {
						const n = summaryByAction.get(a) ?? 0;
						return (
							<div
								key={a}
								className="rounded-[6px] border border-zinc-200 bg-white p-3"
								data-testid={`auto-action-block-mark-reject-alert-on-fraud-total-${a}`}
							>
								<span className={actionBadgeClass(a)}>{a}</span>
								<p className="mt-2 text-2xl font-semibold text-zinc-900">
									{n.toLocaleString()}
								</p>
							</div>
						);
					})}
				</div>
				{summaryQuery.error ? (
					<p className="mt-2 text-xs text-red-700" role="alert">
						{summaryQuery.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Record fraud event"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Record fraud event
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					The server maps risk_score to an action using its built-in thresholds.
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="auto-action-block-mark-reject-alert-on-fraud-create-form"
				>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-event-id">
							Event id
						</label>
						<input
							id="f-event-id"
							type="text"
							className={INPUT}
							placeholder="evt-123"
							value={eventId}
							onChange={(e) => setEventId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-action-block-mark-reject-alert-on-fraud-event-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-subject-type">
							Subject type
						</label>
						<select
							id="f-subject-type"
							className={INPUT}
							value={subjectType}
							onChange={(e) =>
								setSubjectType(e.currentTarget.value as FraudSubjectType)
							}
							disabled={createMut.isPending}
							data-testid="auto-action-block-mark-reject-alert-on-fraud-subject-type"
						>
							{SUBJECT_TYPES.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-subject-id">
							Subject id
						</label>
						<input
							id="f-subject-id"
							type="text"
							className={INPUT}
							placeholder="click-abc"
							value={subjectId}
							onChange={(e) => setSubjectId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-action-block-mark-reject-alert-on-fraud-subject-id"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-risk">
							Risk score (0-100)
						</label>
						<input
							id="f-risk"
							type="number"
							min={0}
							max={100}
							className={INPUT}
							value={riskScore}
							onChange={(e) => setRiskScore(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-action-block-mark-reject-alert-on-fraud-risk"
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label className={LABEL} htmlFor="f-reason">
							Reason (optional)
						</label>
						<input
							id="f-reason"
							type="text"
							className={INPUT}
							placeholder="Short reason"
							value={reason}
							onChange={(e) => setReason(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="auto-action-block-mark-reject-alert-on-fraud-reason"
						/>
					</div>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={
								createMut.isPending || !eventId.trim() || !subjectId.trim()
							}
							data-testid="auto-action-block-mark-reject-alert-on-fraud-submit"
						>
							{createMut.isPending ? "Deciding..." : "Decide & record"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p className="mt-3 text-xs text-red-700" role="alert">
						{createMut.error.message}
					</p>
				) : null}
				{createMut.data ? (
					<p
						className="mt-3 text-xs text-green-800"
						data-testid="auto-action-block-mark-reject-alert-on-fraud-create-result"
					>
						{createMut.data.skipped
							? `Skipped (clean) — risk_score=${createMut.data.risk_score}`
							: `Recorded as ${createMut.data.action} (severity=${createMut.data.severity})`}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Recent actions feed"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Recent actions
				</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-action-block-mark-reject-alert-on-fraud-loading"
					>
						Loading...
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-700"
						role="alert"
						data-testid="auto-action-block-mark-reject-alert-on-fraud-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-action-block-mark-reject-alert-on-fraud-empty"
					>
						No actions recorded yet.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="w-full min-w-[640px] text-sm"
							data-testid="auto-action-block-mark-reject-alert-on-fraud-list"
						>
							<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-3 py-2 font-medium">When</th>
									<th className="px-3 py-2 font-medium">Action</th>
									<th className="px-3 py-2 font-medium">Severity</th>
									<th className="px-3 py-2 font-medium">Score</th>
									<th className="px-3 py-2 font-medium">Subject</th>
									<th className="px-3 py-2 font-medium">Event</th>
									<th className="px-3 py-2 font-medium">Reason</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200 bg-white">
								{items.map((row) => (
									<tr
										key={row.id}
										data-testid={`auto-action-block-mark-reject-alert-on-fraud-row-${row.id}`}
									>
										<td className="px-3 py-2 font-mono text-xs text-zinc-600">
											{formatTime(row.created_at)}
										</td>
										<td className="px-3 py-2">
											<span className={actionBadgeClass(row.action)}>
												{row.action}
											</span>
										</td>
										<td className="px-3 py-2">
											<span className={severityBadgeClass(row.severity)}>
												{row.severity}
											</span>
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.risk_score}
										</td>
										<td className="px-3 py-2 text-xs text-zinc-700">
											<span className="font-medium">{row.subject_type}</span>
											<span className="text-zinc-400"> / </span>
											<span className="font-mono">{row.subject_id}</span>
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-600">
											{row.event_id}
										</td>
										<td className="px-3 py-2 text-xs text-zinc-700">
											{row.reason || (
												<span className="text-zinc-400">-</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AutoActionBlockMarkRejectAlertOnFraudPage;
